import {
  Stack,
  Typography,
  Box,
  Chip,
  Button,
  IconButton,
} from "@mui/material";
import { useParams, useNavigate } from "react-router-dom";
import { Frame } from "../../components/layouts/Frame";
import { DataTable } from "../../components/global/Table/DataTable";
import { Link } from "../../components/global/Link";
import { DataTableHeadCellData } from "../../components/global/Table/DataTableHeader";
import { useEffect, useRef, useState } from "react";
import { useAuth } from "../../contexts/AuthContext";
import { useAPI } from "../../hooks/useAPI";
import { UserData } from "../../types";
import { DownloadIcon } from "../../assets/svgs/DownloadIcon";
import EditSquareIcon from "@mui/icons-material/EditSharp";
import DeleteIcon from "@mui/icons-material/Delete";

const headCells: DataTableHeadCellData[] = [
  {
    id: "name",
    disablePadding: true,
    label: "Name",
    align: "left",
    sortable: true,
    searchable: true,
  },
  {
    id: "email",
    disablePadding: true,
    label: "Email",
    align: "left",
    cellAlign: "left",
    sortable: true,
    searchable: true,
  },
  {
    id: "type",
    disablePadding: true,
    label: "Type",
    align: "left",
    cellAlign: "left",
  },
  {
    id: "role",
    disablePadding: true,
    label: "Role",
    align: "left",
    cellAlign: "left",
  },
  {
    id: "phoneNumber",
    disablePadding: true,
    label: "Phone Number",
    align: "left",
    cellAlign: "left",
  },
  {
    id: "actions",
    disablePadding: false,
    label: "Actions",
    width: "50px",
  },
];

interface UserTableData {
  id: any;
  name: any;
  email: any;
  type: any;
  role: any;
  phoneNumber: any;
  actions: JSX.Element;
}

const Roles = [
  "Admin level access on the app",
  "Access to web admin",
  "Add/edit users",
  "Edit Prices",
];

export const Users = () => {
  const isLoading = useRef(false);
  const [sIsLoading, setSIsLoading] = useState(false);
  const { isAuthenticated } = useAuth();
  const { fetchData, token } = useAPI();
  const [rows, setRows] = useState<UserTableData[]>([]);
  const [users, setUsers] = useState<UserData[]>([]);

  const navigate = useNavigate();

  const refetchUsers = async () => {
    setSIsLoading(true);
    isLoading.current = true;
    const { errors, data } = await fetchData("/user/get_list");
    setUsers(data ?? []);
    isLoading.current = false;
    setSIsLoading(false);
  };

  const deleteById = async (userId: string) => {
    const { errors, data } = await fetchData(
      "/user/delete",
      { id: [userId] },
      "POST",
    );

    refetchUsers();
  };

  const getRows = (users: UserData[]) => {
    const result: UserTableData[] = [];
    for (let i = 0; i < users.length; i++) {
      const roles: number[] = Array.isArray(users[i].roles)
        ? users[i].roles
        : [];

      let roleStr = "";

      if (roles?.includes(0)) {
        roleStr += "Admin level access on the app, ";
      }

      if (roles?.includes(1)) {
        roleStr += "Access to web admin, ";
      }

      if (roles?.includes(2)) {
        roleStr += "Add/edit users, ";
      }

      if (roles?.includes(3)) {
        roleStr += "Edit Prices";
      }

      result.push({
        id: `${users[i].id}`,
        name: <Typography>{users[i].name}</Typography>,
        email: <Typography>{users[i].email}</Typography>,
        type: (
          <Typography>
            {users[i].type === "deckboard" ? "Dekboard" : "EcoDek"}
          </Typography>
        ),
        role: <Typography>{roleStr}</Typography>,
        phoneNumber: <Typography>{users[i].phone_number}</Typography>,
        actions: (
          <Box
            sx={{
              display: "flex",
            }}>
            <IconButton
              aria-label="edit"
              onClick={() => navigate(`/users/edit/${users[i].id}`)}>
              <EditSquareIcon />
            </IconButton>
            <IconButton
              aria-label="delete"
              onClick={() => deleteById(users[i]?.id ?? "")}>
              <DeleteIcon />
            </IconButton>
          </Box>
        ),
      });
    }
    return result;
  };

  useEffect(() => {
    if (isAuthenticated && !isLoading.current) {
      refetchUsers();
    }
  }, [isAuthenticated]);

  useEffect(() => {
    setRows(getRows(users));
  }, [users]);

  return (
    <Frame title="Users">
      <Stack>
        <Stack display="flex" flexDirection="row" mb={2}>
          <Button variant="contained" onClick={() => navigate("/users/new")}>
            Add User
          </Button>
        </Stack>
        <DataTable
          title="Users"
          headCells={headCells}
          rows={rows}
          isLoading={sIsLoading}
          hideIdCheckbox={true}
          hideDeleteButton={true}
        />
      </Stack>
    </Frame>
  );
};
